import { useTranslation } from '@kostbar/i18n'
import { useLocationStore } from '@/shared/store/location-store'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'

interface ScopeLineProps {
  /** The period, already worded by the caller — only it knows whether a custom range is set. */
  period: string
  /** How many articles the report is narrowed to; 0 means all of them. */
  articleCount?: number
}

/**
 * `31.08. – 06.09.2026 · 2 Standorte · 3 Artikel` — what this page is actually showing
 * (CHG-2026-09-article-location-filter).
 *
 * The filters sit in a row that collapses into a button on a phone and disappears entirely when the
 * page is printed. Without this line a printed report carrying three of five hundred articles looks
 * exactly like one carrying all of them, and the reader has no way to tell which they are holding.
 *
 * Locations are named while there are few enough to read, and counted after that: three names are
 * the answer, seven are a wall.
 */
export function ScopeLine({ period, articleCount = 0 }: ScopeLineProps) {
  const { t } = useTranslation('reports')
  const stored = useLocationStore((s) => s.restaurantIds)
  const { data } = useRestaurants()

  const locations = (data?.items ?? []).filter((r) => stored.includes(r.id))

  const parts = [period]
  if (locations.length > 0) {
    parts.push(
      locations.length <= 2
        ? locations.map((r) => r.name).join(' · ')
        : t('location.count', { value: locations.length }),
    )
  }
  if (articleCount > 0) {
    parts.push(t('articleFilter.count', { value: articleCount }))
  }

  return (
    <p className="mt-1 text-sm text-muted-foreground">{parts.join(' · ')}</p>
  )
}
